/**
 * Which till a pack belongs to.
 *
 * ⚠️ A POS PACK WITH NO TILL IS DEAD WEIGHT, AND IT USED TO BE THE ONLY PACK A
 * CASHIER GOT.
 *
 * Measured in the real Electron build against the live site: `prepareOfflineForUser`
 * runs as soon as a session exists, which is BEFORE `cashier.store` has listed and
 * activated a till. The pack was therefore written with `scope: null` — 2,331 items,
 * 1,893 barcodes, 2,584 customers, 0.83 MB — and `readDataset` refuses a scope
 * mismatch by design, so the till then read *nothing* from it. A cashier who signed
 * in and lost the connection inside the next three minutes had an empty catalogue
 * and could not sell at all, while the device believed it held a complete pack.
 *
 * So the scope is resolved in one place, and both the downloader (skip when there
 * is no till) and the schedule (re-download when the till changes, and never treat
 * another till's data as fresh) ask the same question.
 *
 * Kept free of imports from `packs.ts` on purpose: `packs.ts` must stay store-free
 * so the storage layer can be tested without Pinia.
 */

import { useCashierStore } from "@/stores/cashier.store";

/**
 * The scope a pack's datasets are stamped with, or `null` for packs that are not
 * till-specific.
 *
 * Reads the store lazily and tolerates its absence: this is called from module
 * scope during boot, where Pinia may not be installed yet.
 */
export function activePackScope(pack: string): string | null {
  if (pack !== "pos")
    return null;

  try {
    return useCashierStore().activeProfileName || null;
  }
  catch {
    return null;
  }
}

/** Reason code used when a pack is skipped rather than failed. */
export const NO_TILL_SELECTED = "no-till-selected";
